import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import { CONFIG } from '../../config/config';

interface ReviewStatusTabProps {
  parentEntityId: number;
  childCategory: string;
  isLeafEntity?: boolean;
  /** PROGRAM, ACCOUNT, etc. — enables project-level roll-up across descendants */
  entityTypeCategory?: string | null;
}

interface StatusMatrixItem {
  definition_name: string;
  draft: number;
  in_progress: number;
  submitted: number;
  correction_pending: number;
  reviewed: number;
  closed: number;
  total: number;
}

interface ReviewRollupCounts {
  total: number;
  draft: number;
  in_progress: number;
  submitted: number;
  correction_pending: number;
  reviewed: number;
  closed: number;
  active: number;
  submission_overdue: number;
  closure_overdue: number;
}

interface ReviewProjectRollupItem extends ReviewRollupCounts {
  entity_id: number;
  entity_name: string;
}

interface ReviewProjectRollupResponse {
  parent_entity_id: number;
  project_count: number;
  overall: ReviewRollupCounts;
  project_summary: ReviewProjectRollupItem[];
}

const STATUS_COLORS: Record<string, string> = {
  DRAFT: '#9e9e9e',
  'IN-PROGRESS': '#2196f3',
  SUBMITTED: '#ff9800',
  'CORRECTION-PENDING': '#f44336',
  REVIEWED: '#4caf50',
  CLOSED: '#607d8b',
};

function usesProjectRollup(
  isLeafEntity: boolean,
  entityTypeCategory?: string | null
): boolean {
  return (
    !isLeafEntity &&
    Boolean(entityTypeCategory) &&
    entityTypeCategory !== 'PROJECT'
  );
}

const ReviewStatusTab: React.FC<ReviewStatusTabProps> = ({
  parentEntityId,
  childCategory,
  isLeafEntity = false,
  entityTypeCategory = null,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusMatrix, setStatusMatrix] = useState<StatusMatrixItem[]>([]);
  const [projectRollup, setProjectRollup] = useState<ReviewProjectRollupResponse | null>(null);
  const [rollupLoading, setRollupLoading] = useState(false);
  const [rollupError, setRollupError] = useState<string | null>(null);

  const showProjectRollup = usesProjectRollup(isLeafEntity, entityTypeCategory);

  useEffect(() => {
    if (!showProjectRollup || !parentEntityId) {
      setProjectRollup(null);
      setRollupError(null);
      return;
    }
    const fetchRollup = async () => {
      setRollupLoading(true);
      setRollupError(null);
      try {
        const res = await fetch(
          `${CONFIG.SERVER_URL}/dashboard/reviews/project-rollup?parent_entity_id=${parentEntityId}`
        );
        if (!res.ok) throw new Error('Failed to fetch review project roll-up');
        setProjectRollup((await res.json()) as ReviewProjectRollupResponse);
      } catch (err) {
        setRollupError(err instanceof Error ? err.message : 'Failed to load roll-up');
        setProjectRollup(null);
      } finally {
        setRollupLoading(false);
      }
    };
    fetchRollup();
  }, [parentEntityId, showProjectRollup]);

  useEffect(() => {
    if (isLeafEntity) {
      const fetchEntityReviews = async () => {
        setLoading(true);
        setError(null);
        try {
          const reviewResponse = await fetch(
            `${CONFIG.SERVER_URL}/dashboard/reviews/entity?entity_id=${parentEntityId}`
          );
          if (!reviewResponse.ok) throw new Error('Failed to fetch review status data');
          const reviewData = await reviewResponse.json();
          setStatusMatrix(reviewData.status_matrix || []);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to fetch data');
        } finally {
          setLoading(false);
        }
      };
      fetchEntityReviews();
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const reviewResponse = await fetch(
          `${CONFIG.SERVER_URL}/dashboard/reviews/dashboard?parent_entity_id=${parentEntityId}&child_category=${encodeURIComponent(childCategory)}`
        );
        if (!reviewResponse.ok) throw new Error('Failed to fetch review status data');
        const reviewData = await reviewResponse.json();
        setStatusMatrix(reviewData.status_matrix || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    if (parentEntityId && (childCategory || isLeafEntity)) {
      fetchData();
    }
  }, [parentEntityId, childCategory, isLeafEntity]);

  if (loading && !showProjectRollup) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !projectRollup) {
    return <Alert severity="error">{error}</Alert>;
  }

  const overall = projectRollup?.overall;

  return (
    <Box>
      {showProjectRollup && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Review roll-up across {projectRollup?.project_count ?? '…'} projects
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Aggregates all reviews on descendant projects (not only direct children of this
            entity). Overdue uses each review definition&apos;s submission and closure windows.
          </Typography>
          {rollupLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {rollupError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {rollupError}
            </Alert>
          )}
          {!rollupLoading && !rollupError && overall && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={4} md={2}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="text.secondary">
                      Active
                    </Typography>
                    <Typography variant="h6">{overall.active}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="text.secondary">
                      In progress
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {overall.in_progress}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="text.secondary">
                      Submitted
                    </Typography>
                    <Typography variant="h6">{overall.submitted}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="text.secondary">
                      Past submit due
                    </Typography>
                    <Typography
                      variant="h6"
                      color={overall.submission_overdue > 0 ? 'error' : 'text.primary'}
                    >
                      {overall.submission_overdue}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="text.secondary">
                      Past closure due
                    </Typography>
                    <Typography
                      variant="h6"
                      color={overall.closure_overdue > 0 ? 'error' : 'text.primary'}
                    >
                      {overall.closure_overdue}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="text.secondary">
                      Closed
                    </Typography>
                    <Typography variant="h6">{overall.closed}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
          {!rollupLoading && !rollupError && projectRollup && projectRollup.project_summary.length > 0 && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                By project
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Project</TableCell>
                      <TableCell align="right">Active</TableCell>
                      <TableCell align="right">In progress</TableCell>
                      <TableCell align="right">Submitted</TableCell>
                      <TableCell align="right">Submit overdue</TableCell>
                      <TableCell align="right">Closure overdue</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {projectRollup.project_summary.map((row) => (
                      <TableRow key={row.entity_id}>
                        <TableCell>{row.entity_name}</TableCell>
                        <TableCell align="right">{row.active}</TableCell>
                        <TableCell align="right">{row.in_progress}</TableCell>
                        <TableCell align="right">{row.submitted}</TableCell>
                        <TableCell align="right">
                          <Typography
                            color={row.submission_overdue > 0 ? 'error' : 'text.secondary'}
                          >
                            {row.submission_overdue}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            color={row.closure_overdue > 0 ? 'error' : 'text.secondary'}
                          >
                            {row.closure_overdue}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{row.total}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Review Status by Definition
                {showProjectRollup ? ' (direct children)' : ''}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Definition</TableCell>
                      <TableCell align="center">Draft</TableCell>
                      <TableCell align="center">In Progress</TableCell>
                      <TableCell align="center">Submitted</TableCell>
                      <TableCell align="center">Correction Pending</TableCell>
                      <TableCell align="center">Reviewed</TableCell>
                      <TableCell align="center">Closed</TableCell>
                      <TableCell align="center">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statusMatrix.length > 0 ? (
                      statusMatrix.map((row) => (
                        <TableRow key={row.definition_name}>
                          <TableCell>{row.definition_name}</TableCell>
                          <TableCell align="center">
                            {row.draft > 0 && (
                              <Chip
                                label={row.draft}
                                size="small"
                                sx={{ bgcolor: STATUS_COLORS.DRAFT, color: 'white' }}
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {row.in_progress > 0 && (
                              <Chip
                                label={row.in_progress}
                                size="small"
                                sx={{ bgcolor: STATUS_COLORS['IN-PROGRESS'], color: 'white' }}
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {row.submitted > 0 && (
                              <Chip
                                label={row.submitted}
                                size="small"
                                sx={{ bgcolor: STATUS_COLORS.SUBMITTED, color: 'white' }}
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {row.correction_pending > 0 && (
                              <Chip
                                label={row.correction_pending}
                                size="small"
                                sx={{
                                  bgcolor: STATUS_COLORS['CORRECTION-PENDING'],
                                  color: 'white',
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {row.reviewed > 0 && (
                              <Chip
                                label={row.reviewed}
                                size="small"
                                sx={{ bgcolor: STATUS_COLORS.REVIEWED, color: 'white' }}
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {row.closed > 0 && (
                              <Chip
                                label={row.closed}
                                size="small"
                                sx={{ bgcolor: STATUS_COLORS.CLOSED, color: 'white' }}
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={row.total} size="small" variant="outlined" />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Typography color="text.secondary" sx={{ py: 2 }}>
                            No review data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default ReviewStatusTab;
